import { CircularProgress } from "../legacy-ui/materialCompat";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDropDownIcon } from "../legacy-ui/icons";
import classNames from "classnames";
import smoothScroll from "../../utils/animateScroll";
import { useInputMode } from "../../utils/hooks/useInputMode";

const FocusableSelectItem = ({
  value,
  label,
  onBlur,
  onEnterPress,
  onFocus,
  setExpanded,
  focusable,
  shouldFocus = false,
}) => {
  const _onEnterPress = () => {
    onEnterPress(value);
    if (typeof onBlur === "function") onBlur();
    if (typeof setExpanded === "function") setExpanded();
  };

  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress: _onEnterPress,
    onFocus,
    focusable,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  useEffect(() => {
    if (focusable && shouldFocus) {
      focusSelf();
    }
  }, [shouldFocus, focusable]);

  return (
    <div
      ref={ref}
      onMouseEnter={handleMouseEnter}
      className={"legacy-focusable-select-item" + (focused ? " is-focused" : "")}
      onClick={_onEnterPress}
    >
      {label}
    </div>
  );
};

const FocusableSelectBoundary = ({
  onBlur,
  value,
  options,
  onChange,
  label,
  className,
  instanceFocusKey,
  isLoading,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [softFocus, setSoftFocus] = useState(false);
  const [menuTopIndex, setMenuTopIndex] = useState(0);
  const childRef = useRef(false);
  const innerRef = useRef();
  const safeOptions = Array.isArray(options) ? options : [];
  const optionCount = safeOptions.length;
  const visibleOptionCount = 4;
  const menuHeight = expanded
    ? isLoading
      ? 72
      : optionCount <= visibleOptionCount
      ? optionCount * 72
      : 288
    : 0;
  const maxMenuTopIndex = Math.max(0, optionCount - visibleOptionCount);
  const clampedMenuTopIndex = Math.min(menuTopIndex, maxMenuTopIndex);
  const showScrollUp =
    expanded &&
    !isLoading &&
    optionCount > visibleOptionCount &&
    clampedMenuTopIndex > 0;
  const showScrollDown =
    expanded &&
    !isLoading &&
    optionCount > visibleOptionCount &&
    clampedMenuTopIndex + visibleOptionCount < optionCount;

  const { ref, focusKey, hasFocusedChild, focusSelf } = useFocusable({
    trackChildren: true,
    focusKey: instanceFocusKey,
    onFocus: () => setSoftFocus(true),
    onBlur: () => {
      setSoftFocus(false);
      setTimeout(() => {
        if (!childRef.current) {
          setSoftFocus(false);
          setExpanded(false);
          setMenuTopIndex(0);
        }
      }, 250);
    },
    onEnterPress: () => setExpanded(true),
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnterBoundary = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  useEffect(() => {
    childRef.current = hasFocusedChild;
  }, [hasFocusedChild]);

  useEffect(() => {
    if (!expanded) {
      setMenuTopIndex(0);
    } else if (menuTopIndex > maxMenuTopIndex) {
      setMenuTopIndex(maxMenuTopIndex);
    }
  }, [expanded, maxMenuTopIndex, menuTopIndex]);

  const ensureFocusedOptionVisible = useCallback(
    (index) => {
      if (!innerRef.current) return;

      const itemHeight = 72;
      const currentTopIndex = Math.round(
        (innerRef.current.scrollTop || 0) / itemHeight
      );
      const firstVisibleIndex = currentTopIndex;
      const lastVisibleIndex = firstVisibleIndex + visibleOptionCount - 1;
      var targetTopIndex = currentTopIndex;

      if (index <= firstVisibleIndex && index > 0) {
        targetTopIndex = currentTopIndex - 1;
      } else if (index >= lastVisibleIndex && index < optionCount - 1) {
        targetTopIndex = currentTopIndex + 1;
      } else if (index < firstVisibleIndex) {
        targetTopIndex = index;
      } else if (index > lastVisibleIndex) {
        targetTopIndex = index - (visibleOptionCount - 1);
      }

      targetTopIndex = Math.max(0, Math.min(targetTopIndex, maxMenuTopIndex));
      const targetScroll = targetTopIndex * itemHeight;
      setMenuTopIndex(targetTopIndex);
      smoothScroll(innerRef, targetScroll, 250);
    },
    [maxMenuTopIndex, optionCount, visibleOptionCount]
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        onMouseEnter={handleMouseEnterBoundary}
        onClick={() => {
          setExpanded(!expanded);
          if (expanded) {
            setMenuTopIndex(0);
          }
        }}
        className={classNames(
          "legacy-focusable-select",
          softFocus && "is-focused",
          expanded && "is-expanded",
          className && className
        )}
      >
        <div className="legacy-focusable-select-trigger">
          <ArrowDropDownIcon
            fontSize="large"
            className="legacy-focusable-select-icon"
            style={{
              transform: expanded ? "rotate(180deg)" : "rotate(0)",
            }}
          />
          <span className="legacy-focusable-select-label">
            {safeOptions.filter((x) => x.value === value)?.[0]?.label || label}
          </span>
        </div>
        {showScrollUp && (
          <div className="legacy-focusable-select-scroll-indicator is-top">
            <ArrowDropDownIcon
              fontSize="large"
              className="legacy-focusable-select-scroll-icon is-up"
              style={{
                transform: "rotate(180deg)",
              }}
            />
          </div>
        )}
        {showScrollDown && (
          <div className="legacy-focusable-select-scroll-indicator is-bottom">
            <ArrowDropDownIcon
              fontSize="large"
              className="legacy-focusable-select-scroll-icon"
            />
          </div>
        )}
        <div
          ref={innerRef}
          className="legacy-focusable-select-menu"
          style={{ height: menuHeight }}
        >
          <div
            className="legacy-focusable-select-menu-inner"
            style={{ height: menuHeight }}
          >
            {isLoading ? (
              <div className="legacy-focusable-select-loading">
                <CircularProgress color="error" />
              </div>
            ) : (
              safeOptions.map((item, index) => (
                <FocusableSelectItem
                  key={item.value + index}
                  label={item.label}
                  value={item.value}
                  onFocus={() => {
                    ensureFocusedOptionVisible(index);
                  }}
                  onEnterPress={onChange}
                  shouldFocus={expanded && index === 0}
                  setExpanded={() => {
                    setSoftFocus(false);
                    setExpanded(false);
                  }}
                  focusable={expanded}
                  onBlur={onBlur}
                />
              ))
            )}

          </div>
        </div>
      </div>
    </FocusContext.Provider >
  );
};

export default FocusableSelectBoundary;
